import type { ReactNode } from "react";
import { Alert } from "@mui/material";
import { spacing } from "../../styles/theme";
import { useSpecsEditorContextValue } from "./useSpecsEditor";
import SpecsSettingsPage from "./SpecsSettingsPage";
import GenericSpecsHistoryPage from "./GenericSpecsHistoryPage";
import SpecsPositionEditorPage from "./SpecsPositionEditorPage";
import SpecsMetadataPage from "./SpecsMetadataPage";
import SpecsPartsPage from "./SpecsPartsPage";
import SpecsElementsPage from "./SpecsElementsPage";
import SpecsStylesPage from "./SpecsStylesPage";
import SpecsShippingPage from "./SpecsShippingPage";
import SpecsGlobalPage from "./SpecsGlobalPage";
import SpecsDesignEditorPage from "./SpecsDesignEditorPage";
import SpecsEmptyStatePage from "./SpecsEmptyStatePage";
import SpecsLayerSpecsPage from "./SpecsLayerSpecsPage";
import SpecsImportPage from "./ImportPage/SpecsImportPage";

export default function SpecsEditorPageRouter() {
  const {
    selectedPanelKind,
    editor,
    revisionHistory: { historyPageProps },
    handleSelectDesign,
  } = useSpecsEditorContextValue();

  const mapSelectedPanelKindToPage: Record<string, ReactNode> = {
    settings: <SpecsSettingsPage />,
    import: <SpecsImportPage />,
    history: (
      <GenericSpecsHistoryPage
        specType="product"
        title="Revision"
        specName={editor.identity.productName || undefined}
        productVersion={editor.identity.productVersion || undefined}
        subtitle={
          editor.identity.productName && editor.identity.productVersion
            ? `${editor.identity.productName} ${editor.identity.productVersion}`
            : undefined
        }
        onBack={handleSelectDesign}
        historyState={{
          revisions: historyPageProps.revisions,
          revisionDiffs: historyPageProps.revisionDiffs,
          loading: historyPageProps.loading,
          error: historyPageProps.error,
          pageIndex: historyPageProps.pageIndex,
          hasPreviousPage: historyPageProps.hasPreviousPage,
          hasNextPage: historyPageProps.hasNextPage,
          activeRevisionId: historyPageProps.loadedRevisionId,
          loadingRevisionId: historyPageProps.loadingRevisionId,
          publishingRevisionId: historyPageProps.publishingRevisionId,
          onPreviousPage: historyPageProps.onPreviousPage,
          onNextPage: historyPageProps.onNextPage,
          onLoadRevision: historyPageProps.onLoadRevision,
          onPublishRevision: historyPageProps.onPublishRevision,
          onExpandRevision: historyPageProps.onExpandRevision,
        }}
      />
    ),
    empty: <SpecsEmptyStatePage />,
    positions: <SpecsPositionEditorPage />,
    metadata: <SpecsMetadataPage />,
    parts: <SpecsPartsPage />,
    elements: <SpecsElementsPage />,
    styles: <SpecsStylesPage />,
    layerSpecs: <SpecsLayerSpecsPage />,
    shipping: <SpecsShippingPage />,
    global: <SpecsGlobalPage />,
    layer: <SpecsDesignEditorPage />,
  };

  if (mapSelectedPanelKindToPage[selectedPanelKind]) {
    return mapSelectedPanelKindToPage[selectedPanelKind];
  }

  return (
    <Alert severity="warning" sx={{ mb: spacing.groupingDesktop }}>
      Selected layer not found. Select another from the sidebar.
    </Alert>
  );
}
